import type { AdoptedSignature, LetterDoc, PageRect } from "@gsignatures/core";
import type { CSSProperties, ReactNode } from "react";
import { Icon } from "../components/Icon";
import { SigInk } from "../components/SigInk";
import { longDate, stamp } from "../lib/format";
import type { Who } from "../strings";
import { S } from "../strings";

export const rectStyle = (r: Pick<PageRect, "l" | "t" | "w" | "h">): CSSProperties => ({
  left: `${r.l}%`,
  top: `${r.t}%`,
  width: `${r.w}%`,
  height: `${r.h}%`,
});

export interface LetterOverlayOpts {
  doc: LetterDoc;
  w: Who;
  /** read: a quiet hint where you'll sign · sign: the "Sign here" flag · signed: your signature. */
  mode: "read" | "sign" | "signed";
  signature?: AdoptedSignature | undefined;
  signerName: string;
  signedAt?: string | undefined;
  /** Ink the signature in (only right after signing). */
  animate?: boolean;
  onFlag?: () => void;
}

/** What's drawn over the letter's pages: the merged-in values, and the signer's spots. */
export function letterOverlay(o: LetterOverlayOpts) {
  return (page: number, aspect: number, placeholder: boolean): ReactNode => {
    const merged = placeholder ? [] : o.doc.mergedSpots.filter((m) => m.page === page);
    const spots = o.doc.spots.filter((s) => s.page === page && s.role === "signer");
    return (
      <>
        {merged.map((m) => (
          <span key={`${m.key}-${m.l}-${m.t}`} className="merge-spot" style={rectStyle(m)} title={S.offer.filledBy(o.w)} aria-hidden="true" />
        ))}
        {spots.map((s) => {
          const style = rectStyle(s);
          if (s.kind === "signature") {
            const fontSize = `${(s.h * aspect * 0.78).toFixed(2)}cqw`;
            if (o.mode === "signed") {
              return (
                <div key={s.id} className="spot spot-sig signed" style={style} data-focus="true">
                  <SigInk sig={o.signature} fontSize={fontSize} animate={o.animate} />
                  {o.signedAt && <span className="spot-stamp">{S.sign.eSigned(stamp(o.signedAt))}</span>}
                </div>
              );
            }
            if (o.mode === "sign") {
              return (
                <div key={s.id} className="spot spot-sig" style={style} data-focus="true">
                  <button type="button" className="flag-here" onClick={o.onFlag} tabIndex={-1} aria-hidden="true">
                    <Icon n="pen" /> {S.tax.signHere}
                  </button>
                </div>
              );
            }
            return (
              <div key={s.id} className="spot spot-sig ghost" style={style}>
                <span>{S.offer.signLater}</span>
              </div>
            );
          }
          if (s.kind === "date") {
            return (
              <div key={s.id} className={`spot spot-text ${o.mode === "signed" ? "" : "ghost"}`} style={style}>
                {o.mode === "signed" && o.signedAt ? longDate(o.signedAt) : S.offer.dateLater}
              </div>
            );
          }
          return (
            <div key={s.id} className={`spot spot-text ${o.mode === "signed" ? "" : "ghost"}`} style={style}>
              {o.signerName}
            </div>
          );
        })}
      </>
    );
  };
}
